import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, KeyRound, Pencil, Loader2, Power, CheckCircle2 } from 'lucide-react';
import {
  equityLiveTradingService as svc,
  type EquityLiveSettings,
  type EquityLiveAccount,
} from '@/services/equityLiveTradingService';

function maskApiKey(key: string | null | undefined): string {
  if (!key) return '—';
  return key.length <= 12 ? key : `${key.slice(0, 8)}…${key.slice(-4)}`;
}

/**
 * Configuration → Equity Live Trading.
 * Manages the user's REAL-MONEY Alpaca (equity) credentials + the per-user live
 * kill switch (default OFF). Deliberately separate from the paper panel so a
 * paper action can never reach the live account.
 */
export const EquityLiveTradingSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<EquityLiveSettings | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [account, setAccount] = useState<EquityLiveAccount | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);

  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await svc.getSettings();
      setSettings(s);
    } finally {
      setLoaded(true);
    }
    // Account is best-effort — a 404 just means "no creds / not connected yet".
    try {
      setAccount(await svc.getAccount());
      setAccountError(null);
    } catch (e: any) {
      setAccount(null);
      setAccountError(e?.message || 'not connected');
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const enabled = !!settings?.liveTradingEnabled;
  const hasCreds = !!settings?.apiKey;

  const applyToggle = async (next: boolean) => {
    setToggling(true);
    setToggleError(null);
    try {
      await svc.setEnabled(next);
      setSettings((s) => (s ? { ...s, liveTradingEnabled: next } : s));
      // Re-sync from the server so a backend override (e.g. master off) shows
      // immediately rather than drifting until the next manual refresh.
      void refresh();
    } catch (e: any) {
      setToggleError(e?.message || 'Failed to update kill switch');
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="space-y-3 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">Equity Live Trading</h2>
        <p className="text-xs text-muted-foreground">
          Connect your REAL-MONEY Alpaca account and arm live equity trading.
        </p>
      </div>

      {/* Real-money warning */}
      <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
        <span className="text-muted-foreground">
          Orders placed with these credentials use <span className="font-semibold">real money</span> on
          Alpaca's live environment — separate from your paper account. The kill switch is OFF by
          default and the system master switch must also be on for any order to fire.
        </span>
      </div>

      {/* Live kill switch */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Power className="h-4 w-4 text-primary" />
            Live Trading (my account)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {settings && !settings.masterEnabled && (
            <div className="text-xs text-amber-600 dark:text-amber-400 p-3 border border-amber-500/30 rounded-md bg-amber-500/5 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>System master switch is OFF — your toggle has no effect until an admin turns it on.</span>
            </div>
          )}
          <div className="rounded-md border bg-card/50 p-3 flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Real-Money Equity Trading</span>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  enabled ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'
                }`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  {enabled ? 'ARMED' : 'OFF'}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {!hasCreds
                  ? 'Add your live API credentials below first.'
                  : enabled
                    ? 'Live equity orders can fire (subject to the master switch).'
                    : 'Live equity orders are suppressed.'}
              </div>
              {toggleError && (
                <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {toggleError}
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant={enabled ? 'outline' : 'default'}
              disabled={!hasCreds || toggling}
              onClick={() => void applyToggle(!enabled)}
            >
              {toggling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {enabled ? 'Disarm' : 'Arm live trading'}
            </Button>
          </div>

          {/* Live connection status */}
          {account ? (
            <div className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Connected — account {account.accountNumber} · equity {account.equity ?? '—'}
            </div>
          ) : hasCreds && accountError ? (
            <div className="text-[11px] text-muted-foreground">Live connection: {accountError}</div>
          ) : null}
        </CardContent>
      </Card>

      <LiveCredentialsCard settings={settings} loaded={loaded} onSaved={refresh} />
    </div>
  );
};

const LiveCredentialsCard: React.FC<{
  settings: EquityLiveSettings | null;
  loaded: boolean;
  onSaved: () => Promise<void>;
}> = ({ settings, loaded, onSaved }) => {
  const hasCreds = !!settings?.apiKey;
  const [editing, setEditing] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) setApiKey(settings.apiKey || '');
    if (loaded && !hasCreds) setEditing(true);
  }, [settings, loaded, hasCreds]);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await svc.saveSettings({ apiKey, ...(apiSecret ? { apiSecret } : {}) });
      setApiSecret('');
      setEditing(false);
      await onSaved();
    } catch (e: any) {
      setError(e?.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className={loaded && !hasCreds ? 'border-amber-500/40' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            Live Credentials (Alpaca)
          </span>
          {hasCreds && !editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {!editing && hasCreds && settings && (
          <div className="text-xs">
            <span className="text-muted-foreground">key </span>
            <span className="font-mono text-foreground">{maskApiKey(settings.apiKey)}</span>
            <span className="text-muted-foreground"> · secret </span>
            <span className="font-mono text-foreground">{settings.apiSecretMasked}</span>
          </div>
        )}

        {editing && (
          <div className="space-y-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-muted-foreground uppercase tracking-wider">
                Live API Key {!hasCreds && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder="AKxxxxxxxxxxxxxx"
                className="h-8 w-full max-w-[400px] rounded-md border bg-background px-2 text-xs font-mono outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-muted-foreground uppercase tracking-wider">
                Live API Secret {!hasCreds && <span className="text-red-500">*</span>}
              </label>
              <input
                type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
                placeholder={hasCreds ? '**** keep existing' : 'Paste your live secret'}
                className="h-8 w-full max-w-[400px] rounded-md border bg-background px-2 text-xs font-mono outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {hasCreds && <span className="text-[10px] text-muted-foreground">Leave blank to keep the existing secret.</span>}
            </div>
            {error && (
              <div className="text-xs text-red-500 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {error}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={() => void handleSave()} disabled={saving || !apiKey || (!hasCreds && !apiSecret)}>
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />} Save
              </Button>
              {hasCreds && (
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setApiSecret(''); }} disabled={saving}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EquityLiveTradingSettingsPage;
